/**
 * @file FooterSortableRow.tsx
 * @description Строка списка «Ссылки» нижней полосы подвала (phase19.16,
 * приёмка Р2а — SB118; партия «Юридические документы» — SB142): общая
 * обвязка для ссылок платформы и своих строк — ⋮⋮ (перетаскивание @dnd-kit,
 * только за ручку), текст, свои кнопки строки и «Выше» / «Ниже» (запасной
 * путь для касания и клавиатуры). Кнопки по 48 px; на узкой панели (360)
 * кнопки переносятся под текст, а не сжимают его до слова в строке.
 *
 * Здесь же `WhereNote` — подпись «где задаётся» (SB121).
 */

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowDown, ArrowUp, GripVertical } from "lucide-react";
import type { ReactNode } from "react";
import { Link } from "react-router";

import { Button } from "../../ui/button";
import type { FooterLinkWhere } from "../../../lib/site-view/footer-link-where";
import { cn } from "@/lib/utils";

/** Подпись «где задаётся»: пояснение и ссылка на раздел. */
export function WhereNote({ where }: { where: FooterLinkWhere }) {
  return (
    <span className="type-caption flex flex-wrap items-center gap-x-1 text-on-surface-variant">
      {where.note ? <span>{where.note}{where.href ? " ·" : ""}</span> : null}
      {where.href && where.linkLabel ? (
        <Link to={where.href} className="inline-flex min-h-12 items-center font-medium text-primary hover:underline">
          {where.linkLabel}
        </Link>
      ) : null}
    </span>
  );
}

interface Props {
  /** Ключ строки для перетаскивания: `offer`, `doc:<id>`, `all-documents`. */
  id: string;
  /** Подпись в кнопках («Перетащить «…»», «… выше»). */
  label: string;
  index: number;
  count: number;
  onMove: (from: number, to: number) => void;
  /** Текст строки: название и пометки. */
  children: ReactNode;
  /** Кнопки строки перед «Выше» / «Ниже»: замок, переключатель, ×. */
  controls: ReactNode;
}

export function FooterSortableRow({ id, label, index, count, onMove, children, controls }: Props) {
  const sortable = useSortable({ id });
  return (
    <li
      ref={sortable.setNodeRef}
      style={{ transform: CSS.Transform.toString(sortable.transform), transition: sortable.transition }}
      className={cn("flex min-h-14 flex-wrap items-center gap-x-1 bg-surface-lowest", sortable.isDragging && "relative z-10 rounded-lg shadow-lg")}
    >
      <button
        type="button"
        {...sortable.attributes}
        {...sortable.listeners}
        aria-label={`Перетащить «${label}»`}
        className="inline-flex size-12 shrink-0 cursor-grab touch-none items-center justify-center rounded-md text-on-surface-variant outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <GripVertical aria-hidden className="size-4" />
      </button>
      {/* Основа 0 и минимум 6rem: текст делит строку с кнопками, пока ему хватает места;
          иначе кнопки уходят второй строкой (своя строка с × на 360). */}
      <span className="flex min-w-24 flex-[1_1_0%] flex-col py-1">{children}</span>
      <span className="ml-auto flex shrink-0 items-center">
        {controls}
        <Button variant="ghost" size="icon" className="size-12" aria-label={`«${label}» выше`} disabled={index === 0} onClick={() => onMove(index, index - 1)}>
          <ArrowUp aria-hidden className="size-4" />
        </Button>
        <Button variant="ghost" size="icon" className="size-12" aria-label={`«${label}» ниже`} disabled={index >= count - 1} onClick={() => onMove(index, index + 1)}>
          <ArrowDown aria-hidden className="size-4" />
        </Button>
      </span>
    </li>
  );
}
